import { useState } from 'react';
import api, { formatError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Loader2, PlayCircle } from 'lucide-react';

export function SalesExam({ exam, onFinished }) {
  const [questions, setQuestions] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const start = async () => {
    setBusy(true);
    try { const { data } = await api.get('/sales/onboarding/exam'); setQuestions(data); setAnswers({}); setResult(null); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post('/sales/onboarding/exam', { answers }); setResult(data); setQuestions(null); onFinished(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (exam.passed) return <p className="text-sm text-emerald-700 font-medium" data-testid="exam-passed">Passed · score {exam.best_score}/{exam.total || 30}</p>;
  if (questions) {
    const answered = Object.keys(answers).length;
    return (
      <div className="space-y-4" data-testid="sales-exam">
        {questions.map((q, i) => (
          <div key={q.id} className="rounded-lg border border-brand-border p-4" data-testid={`exam-q-${i + 1}`}>
            <p className="text-sm font-semibold text-brand-text-primary mb-2">{i + 1}. {q.question}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {q.options.map((o, oi) => (
                <button type="button" key={oi} onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                  className={`text-left text-sm px-3 py-2 rounded-md border transition-colors ${answers[q.id] === oi ? 'border-brand-primary bg-blue-50 text-brand-primary font-medium' : 'border-brand-border hover:bg-brand-bg'}`}
                  data-testid={`exam-q-${i + 1}-opt-${oi}`}>{o}</button>
              ))}
            </div>
          </div>
        ))}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-brand-border py-3 flex items-center justify-between">
          <span className="text-sm text-brand-text-secondary" data-testid="exam-answered">{answered} / {questions.length} answered</span>
          <Button onClick={submit} disabled={busy || answered < questions.length} className="bg-brand-primary text-white" data-testid="exam-submit-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : 'Submit Exam'}
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {result && !result.passed && <p className="text-sm text-rose-600" data-testid="exam-failed">You scored {result.score}/{result.total}. {result.required} needed to pass.</p>}
      {exam.last_score != null && !result && <p className="text-sm text-rose-600">Last attempt: {exam.last_score}/{exam.total}. Not passed.</p>}
      {exam.next_attempt_at ? (
        <p className="text-sm text-amber-700" data-testid="exam-retry-at">You can retry after {new Date(exam.next_attempt_at).toLocaleString()}</p>
      ) : (
        <>
          <p className="text-sm text-brand-text-secondary">30 multiple-choice questions on trading basics, company rules and client handling. You need {exam.pass_pct}% to pass. If you fail you can retry after 24 hours.</p>
          <Button onClick={start} disabled={busy} className="bg-brand-primary text-white" data-testid="exam-start-btn">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <><PlayCircle size={15} className="mr-1.5" /> Start Exam</>}
          </Button>
        </>
      )}
    </div>
  );
}
